import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, User, Calendar, CheckCircle, XCircle, Sparkles, FileText, Wand2, Palette, Loader2, AlertCircle, Send } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import { AIPanel, AIButton, AIExplanationBox } from '../components/AIComponents';
import { QuoteSections } from '../components/QuoteSections';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function QuoteDetail() {
  const { id } = useParams();
  const { token, currentOrg, orgId } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionsSummary, setSectionsSummary] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [burnRateInfo, setBurnRateInfo] = useState(null);
  const [requiredProfitPerJob, setRequiredProfitPerJob] = useState(null);

  // Fetch burn rate state to determine if provisional
  const fetchBurnRateInfo = useCallback(async () => {
    if (!orgId || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/business-reality/${orgId}/burn-rate`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBurnRateInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch burn rate info:', err);
    }
  }, [orgId, token]);

  // Fetch what-would-it-take to get required profit baseline
  const fetchRequiredProfit = useCallback(async () => {
    if (!orgId || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/business-reality/${orgId}/what-would-it-take`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Calculate required profit per job from job volume scenario
        if (data.job_volume_scenario?.average_profit_per_job) {
          setRequiredProfitPerJob(data.job_volume_scenario.average_profit_per_job);
        }
      }
    } catch (err) {
      console.error('Failed to fetch required profit:', err);
    }
  }, [orgId, token]);

  const fetchQuote = useCallback(async () => {
    if (!id || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/quotes/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Quote not found');
        } else {
          throw new Error('Failed to load quote');
        }
        return;
      }
      
      const data = await response.json();
      setQuote(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchQuote();
    fetchBurnRateInfo();
    fetchRequiredProfit();
  }, [fetchQuote, fetchBurnRateInfo, fetchRequiredProfit]);

  const handleSendQuote = async () => {
    if (!quote || quote.status !== 'DRAFT') return;
    
    // Check if sections allow acceptance
    if (sectionsSummary && !sectionsSummary.can_accept) {
      alert('Please add sections with margins before sending the quote.');
      return;
    }
    
    setActionLoading('send');
    try {
      const response = await fetch(`${API_URL}/api/quotes/${id}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to send quote');
      }
      
      fetchQuote();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSectionsUpdate = useCallback((summary) => {
    setSectionsSummary(summary);
    // Refresh quote data to get updated totals
    fetchQuote();
  }, [fetchQuote]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: 'var(--text-muted)' }}>
        <Loader2 size={24} className="spin" style={{ marginRight: '8px' }} />
        Loading quote...
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="detail-page">
        <Link to="/quotes" className="back-link">
          <ArrowLeft size={16} />Back to Quotes
        </Link>
        <div style={{ 
          padding: 'var(--space-xl)', 
          textAlign: 'center', 
          color: 'var(--status-error)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-md)'
        }}>
          <AlertCircle size={48} />
          <h2>{error || 'Quote not found'}</h2>
          <p style={{ color: 'var(--text-muted)' }}>The quote you're looking for doesn't exist or you don't have access.</p>
        </div>
      </div>
    );
  }

  const statusColors = {
    DRAFT: 'pending',
    SENT: 'in-progress',
    ACCEPTED: 'completed',
    DECLINED: 'overdue',
    EXPIRED: 'overdue'
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--space-xl)', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Main Content */}
      <div className="detail-page" style={{ maxWidth: 'none' }}>
        <Link to="/quotes" className="back-link" data-testid="back-to-quotes">
          <ArrowLeft size={16} />Back to Quotes
        </Link>

        <div className="detail-header">
          <div className="detail-title-row">
            <div>
              <h1 className="detail-title" data-testid="quote-title">Quote #{id.slice(0, 8)}</h1>
              <p className="detail-subtitle">Job: {quote.job_id?.slice(0, 8)}</p>
            </div>
            <span 
              className={`status-badge ${statusColors[quote.status] || 'pending'}`} 
              style={{ padding: '8px 16px', fontSize: '0.75rem' }}
              data-testid="quote-status"
            >
              {quote.status}
            </span>
          </div>
        </div>

        <div className="detail-grid mb-xl">
          <div className="detail-item">
            <div className="detail-item-label">Quote Date</div>
            <div className="detail-item-value">
              <Calendar size={16} style={{ color: 'var(--accent-primary)', marginRight: '8px' }} />
              {new Date(quote.created_at).toLocaleDateString('en-GB')}
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">VAT Rate</div>
            <div className="detail-item-value">{quote.vat_rate}%</div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Sections</div>
            <div className="detail-item-value">{quote.sections_count || 0}</div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Total Value (inc. VAT)</div>
            <div className="detail-item-value" style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--status-success)' }} data-testid="quote-total-gross">
              £{quote.quote_amount_gross?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>

        {/* Quote Sections - Phase 1A Component */}
        <div className="mb-xl">
          <QuoteSections 
            quoteId={id} 
            quoteStatus={quote.status}
            onSectionsUpdate={handleSectionsUpdate}
          />
        </div>

        {/* Financial Summary - Sticky Totals Panel (Phase 1D) */}
        <GlassPanel 
          title="Financial Summary" 
          titleIcon={ClipboardList} 
          className="mb-xl"
          style={{
            position: 'sticky',
            bottom: 'var(--space-md)',
            zIndex: 10,
            boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingTop: 'var(--space-md)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Total Cost:</span>
              <span style={{ color: 'var(--text-primary)' }} data-testid="total-cost">
                £{quote.quote_total_cost?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Total Revenue (net):</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }} data-testid="net-amount">
                £{quote.quote_amount_net?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Profit:</span>
              <span style={{ color: 'var(--text-primary)' }} data-testid="total-profit">
                £{quote.quote_profit?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: 'var(--space-sm)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Margin:</span>
              <span style={{ color: 'var(--text-primary)' }} data-testid="margin-percentage">
                {quote.quote_margin_percentage?.toFixed(1) || '0'}%
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--glass-border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>VAT ({quote.vat_rate}%):</span>
              <span style={{ color: 'var(--text-primary)' }}>
                £{((quote.quote_amount_gross || 0) - (quote.quote_amount_net || 0)).toFixed(2)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--glass-border)' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Gross Total:</span>
              <span style={{ fontWeight: '700', fontSize: '1.25rem', color: 'var(--text-primary)' }} data-testid="gross-total">
                £{quote.quote_amount_gross?.toFixed(2) || '0.00'}
              </span>
            </div>

            {/* Required Profit Baseline (Phase 1D) */}
            {requiredProfitPerJob !== null && (
              <div style={{ 
                width: '280px', 
                marginTop: 'var(--space-md)', 
                paddingTop: 'var(--space-md)', 
                borderTop: '1px solid var(--glass-border)',
                fontSize: '0.875rem'
              }}>
                <div style={{ marginBottom: 'var(--space-xs)', color: 'var(--text-muted)' }} data-testid="required-profit-line">
                  Required profit per job {burnRateInfo?.is_provisional ? '(provisional)' : '(verified)'}: £{requiredProfitPerJob.toFixed(2)}
                </div>
                {quote.quote_profit < requiredProfitPerJob && (
                  <div 
                    style={{ 
                      color: 'var(--text-muted)',
                      fontSize: '0.8125rem'
                    }}
                    data-testid="profit-shortfall-note"
                  >
                    Below required profit by £{(requiredProfitPerJob - (quote.quote_profit || 0)).toFixed(2)}
                  </div>
                )}
              </div>
            )}
          </div>
        </GlassPanel>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          {quote.status === 'DRAFT' && (
            <button 
              className="btn btn-primary" 
              style={{ flex: 1 }}
              onClick={handleSendQuote}
              disabled={actionLoading === 'send' || (sectionsSummary && !sectionsSummary.can_accept)}
              data-testid="send-quote-btn"
            >
              {actionLoading === 'send' ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Send size={16} />
              )}
              Send Quote
            </button>
          )}
          {quote.status === 'ACCEPTED' && (
            <div style={{ 
              flex: 1, 
              padding: 'var(--space-md)', 
              background: 'rgba(16, 185, 129, 0.1)', 
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--status-success)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <CheckCircle size={16} />
              Quote Accepted {quote.accepted_at && `on ${new Date(quote.accepted_at).toLocaleDateString('en-GB')}`}
            </div>
          )}
          {quote.status === 'DECLINED' && (
            <div style={{ 
              flex: 1, 
              padding: 'var(--space-md)', 
              background: 'rgba(239, 68, 68, 0.1)', 
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--status-error)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <XCircle size={16} />
              Quote Declined {quote.decline_reason && `- ${quote.decline_reason}`}
            </div>
          )}
        </div>

        {quote.has_legacy_section && (
          <div style={{
            marginTop: 'var(--space-md)',
            padding: 'var(--space-sm) var(--space-md)',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--status-warning)',
            fontSize: '0.875rem'
          }}>
            <strong>Note:</strong> This quote was created before the section-based pricing system. 
            A legacy section has been auto-created to maintain data integrity.
          </div>
        )}
      </div>

      {/* AI Actions Side Panel */}
      <div className="ai-side-panel">
        <AIPanel 
          title="AI Actions" 
          description="AI assists with wording, structure, and clarity. Final approval always remains with you."
        >
          <div className="ai-actions-grid single-col">
            <AIButton icon={Wand2} variant="full" tooltip="AI generation will be enabled soon">
              Draft full quote
            </AIButton>
            <AIButton icon={FileText} variant="full" tooltip="AI generation will be enabled soon">
              Add exclusions & assumptions
            </AIButton>
            <AIButton icon={Palette} variant="full" tooltip="AI generation will be enabled soon">
              Apply company branding
            </AIButton>
            <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
              Prepare client-ready proposal
            </AIButton>
          </div>
        </AIPanel>

        <AIExplanationBox>
          AI helps you create professional quotes faster. All suggestions can be edited before sending.
        </AIExplanationBox>
      </div>
    </div>
  );
}

export default QuoteDetail;
